import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  type MouseEvent,
  useState,
  type ForwardedRef,
  type RefObject,
  Children,
  isValidElement,
  cloneElement,
  useRef,
} from 'react';
import { Link as RouterLink } from 'react-router';
import { ChevronLeft, ChevronRight } from '#icons';
import { TabInner, TabsInner, TabsWrapper, TabsViewport, TabScrollButton } from './styles';

export type TabsProps = {
  id: string;
  children: ReactElement<TabProps>[];
  // align with relevant values in justify-content where its applied
  justify?: 'left' | 'center' | 'right' | 'flex-start' | 'flex-end' | 'start' | 'end' | 'normal';
  animate?: boolean;
};

export type TabProps = Pick<TabsProps, 'animate'> & {
  children: ReactNode;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  to?: string;
  active?: boolean;
  disabled?: boolean;
};

export type TabsInnerProps = Omit<TabsProps, 'children'> & SelectorState;

export type SelectorState = {
  offsetLeft: number;
  width: number;
};

export type TabInnerProps = Pick<TabProps, 'active' | 'animate' | 'disabled'> & {
  forwardedRef?: ForwardedRef<HTMLLIElement>;
};

export const Tabs = ({ children, ...props }: TabsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLUListElement>(null);
  const [scrollState, setScrollState] = useState({ overflowing: false, left: false, right: false });
  const [selectorProps, setSelectorProps] = useState<SelectorState>({
    offsetLeft: 0,
    width: 0,
  });

  const updateScrollState = useCallback(() => {
    const container = containerRef.current;
    const viewport = viewportRef.current;

    if (!container || !viewport) return;

    const maxScroll = viewport.scrollWidth - viewport.clientWidth;

    setScrollState({
      // Measure without button widths so controls disappear as soon as all tabs fit.
      overflowing: viewport.scrollWidth > container.clientWidth + 1,
      left: viewport.scrollLeft > 1,
      right: viewport.scrollLeft < maxScroll - 1,
    });
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const viewport = viewportRef.current;
    const tabs = tabsRef.current;

    if (!container || !viewport || !tabs) return;

    const observer = new ResizeObserver(updateScrollState);

    observer.observe(container);
    observer.observe(viewport);
    observer.observe(tabs);
    updateScrollState();

    return () => {
      observer.disconnect();
    };
  }, [updateScrollState]);

  const scrollTabs = (direction: number) => {
    const viewport = viewportRef.current;

    if (viewport) {
      viewport.scrollBy({ left: direction * viewport.clientWidth * 0.8 });
    }
  };

  const callback = useCallback((e: RefObject<HTMLLIElement>) => {
    if (e.current?.offsetWidth) {
      setSelectorProps({ offsetLeft: e.current.offsetLeft, width: e.current.offsetWidth });
    }
  }, []);

  const renderChildren = useCallback(() => {
    return Children.map(children, (child, index) => {
      if (!isValidElement(child)) return null;

      // Typecasting here to be able to pass callback without typescipt (and developers) having to know or care.
      return cloneElement(child, {
        key: `${props.id}-tab-${index}`,
        callback,
        animate: props.animate,
        ['data-testid']: `${props.id}-tab-${index}`,
      } as unknown as TabProps);
    });
  }, [children, callback, props.id, props.animate]);

  return (
    <TabsWrapper ref={containerRef}>
      {scrollState.overflowing && (
        <TabScrollButton
          type="button"
          aria-label="Scroll tabs left"
          aria-controls={props.id}
          disabled={!scrollState.left}
          onClick={() => {
            scrollTabs(-1);
          }}
        >
          <ChevronLeft />
        </TabScrollButton>
      )}
      <TabsViewport ref={viewportRef} onScroll={updateScrollState}>
        <TabsInner
          ref={tabsRef}
          {...selectorProps}
          role="tablist"
          {...props}
          data-testid={props.id}
        >
          {renderChildren()}
        </TabsInner>
      </TabsViewport>
      {scrollState.overflowing && (
        <TabScrollButton
          type="button"
          aria-label="Scroll tabs right"
          aria-controls={props.id}
          disabled={!scrollState.right}
          onClick={() => {
            scrollTabs(1);
          }}
        >
          <ChevronRight />
        </TabScrollButton>
      )}
    </TabsWrapper>
  );
};

export const Tab = ({ children, onClick, to, active, ...props }: TabProps) => {
  const elementRef = useRef<HTMLLIElement>(null);

  if (!onClick && !to) throw Error('Tab must have either an onClick or to (link) prop');

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (props.disabled) return;
    if (onClick) {
      onClick(e);
    }
  };

  useEffect(() => {
    // @ts-expect-error Callback does exist I just don't want anyone to know about it
    const { callback } = props;

    if (active && callback) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      callback(elementRef);
    }
  }, [active, elementRef]);

  return (
    <TabInner ref={elementRef} role="tab" active={active} {...props}>
      <RouterLink {...(onClick ? { onClick: handleClick } : null)} to={to && !onClick ? to : '#'}>
        {children}
      </RouterLink>
    </TabInner>
  );
};
